/* eslint-disable no-console */
/* eslint-disable no-param-reassign */
import { GlobalState } from '@leverege/ui-redux'
import { Project } from '@leverege/api-redux'
import { Attributes } from '@leverege/ui-attributes'
import { Toast } from '@leverege/ui-elements'
// eslint-disable-next-line import/no-extraneous-dependencies
import isEqual from 'fast-deep-equal'

export function getEnumValue( value, type, dVal ) {
  if ( !value ) return dVal
  
  const enumData = getEnumValues( type )
  const enumMap = enumData.reduce( ( prev, curr ) => {
    prev[curr.value] = curr.name
    
    return prev
  }, {} )
  return enumMap[value] || value || dVal
}

export function getEnumValues( type ) {
  return Project( process.env.PROJECT_ID ).enum( type ).getEnum( GlobalState.get() ).values
}

export function getOcrMatchStatusPillVariant( status ) {
  let variant

  if ( !status ) {
    variant = 'InProgress'
    return `plyLayerShopFloorPill${variant}`
  }

  switch ( status ) {
    case 'true':
      variant = 'Success'
      break
    case 'unverifiedTimeout':
      variant = 'UnverifiedTimeout'
      break
    case 'inProgress':
      variant = 'InProgress'
      break
    default:
      variant = ''
  }

  return `plyLayerShopFloorPill${variant}`
}

export function getToleranceStatusPillVariant( status ) {
  let variant

  if ( !status ) {
    variant = 'InProgress'
    return `plyLayerShopFloorPill${variant}`
  }

  switch ( status ) {
    case 'withinTolerance':
      variant = 'Success'
      break
    case 'notWithinTolerance':
      variant = 'Error'
      break
    case 'inProgress':
      variant = 'InProgress'
      break
    case 'unverifiedTimeout':
      variant = 'UnverifiedTimeout'
      break
    default:
      variant = ''
  }

  return `plyLayerShopFloorPill${variant}`
}

const EMPTY_PLY_LAYERS = {
  currentLaserLayer : {
    layers : [],
    groupedLayers : []
  },
  previousLayers : []
}
const PLY_GROUP_REGEXES = [ /(CRACK STOPPER)/, /([A-Z0-9]+)(-|_)?.*/ ]
const PLY_COMPARISON_REGEX = /([A-Z]+)([0-9]*)/

export function groupPlyLayers( items ) {
  if ( !items || items.length === 0 ) {
    return EMPTY_PLY_LAYERS
  }

  const {
    previousLayers,
    ...currentLaserLayer
  } = items.reduce(
    ( prev, curr ) => {
      let status = Attributes.get( 'plyLayer.progress.status', curr )
      const completedAt = Attributes.get( 'plyLayer.progress.completedAt', curr )
      const laserLayer = Attributes.get( 'plyLayer.laserLayer*', curr )
      const actualStep = Attributes.get( 'plyLayer.order.actualStep', curr )
      const lastActualStep = prev.actualStep

      status = completedAt != null ? 'complete' : status

      if ( status === 'inProgress' && ( lastActualStep == null || actualStep === lastActualStep ) ) {
        if ( !prev.laserLayer ) {
          prev.laserLayer = laserLayer
        } else if ( !isEqual( prev.laserLayer, laserLayer ) ) {
          console.warn( 'More than one laserLayer detected in current laser projection' )
        }
        if ( !prev.actualStep ) {
          prev.actualStep = actualStep
        } else if ( prev.actualStep !== actualStep ) {
          console.warn( 'More than one actualStep detected in current laser projection' )
        }
        prev.layers.push( curr )
      } else if ( status === 'complete' ) {
        prev.previousLayers.push( curr )
      }

      return prev
    },
    {
      layers : [],
      laserLayer : null,
      actualStep : null,
      previousLayers : []
    }
  )

  if ( currentLaserLayer.laserLayer == null || currentLaserLayer.actualStep == null ) {
    return EMPTY_PLY_LAYERS
  }
  
  return {
    currentLaserLayer : {
      ...currentLaserLayer,
      groupedLayers : sortPlyGroups( currentLaserLayer.layers )
    },
    previousLayers
  }
}

function sortPlyGroups( items ) {
  const groups = items.reduce( ( prev, plyLayer ) => {
    const name = Attributes.get( 'name', plyLayer )
    const matches = getPlyGroupMatch( name )

    if ( matches ) {
      const [ , group ] = matches
      if ( group ) {
        const groupData = prev[group]
        if ( groupData ) {
          prev[group].push( plyLayer )
        } else {
          prev[group] = [ plyLayer ]
        }
      }
    }

    return prev
  }, {} )

  const processed = Object.entries( groups )
    .sort( ( [ a ], [ b ] ) => {
      const [ , aAlpha, aNumberRaw ] = a.match( PLY_COMPARISON_REGEX )
      const [ , bAlpha, bNumberRaw ] = b.match( PLY_COMPARISON_REGEX )
      const aNumber = parseInt( aNumberRaw )
      const bNumber = parseInt( bNumberRaw )

      if ( aAlpha > bAlpha ) {
        return -1
      }
      
      if ( aAlpha < bAlpha ) {
        return 1
      }

      if ( aNumber > bNumber ) {
        return 1
      }
      
      if ( aNumber < bNumber ) {
        return -1
      }

      return 1
    } )

  processed.forEach( ( [ key, value ] ) => {
    value.sort( ( a, b ) => {
      const aName = Attributes.get( 'name', a )
      const bName = Attributes.get( 'name', b )

      if ( aName > bName ) {
        return 1
      }

      return -1
    } )
  } )

  return processed
}

function getPlyGroupMatch( str ) {
  for ( let i = 0; i < PLY_GROUP_REGEXES.length; i++ ) {
    const regex = PLY_GROUP_REGEXES[i]
    const matches = str.match( regex )

    if ( matches ) {
      return matches
    }
  }

  return null
}

export const resizeImage = async ( file, maxWidth = 1024, maxHeight = 1024, quality = 0.8 ) => {
  return new Promise( ( resolve ) => {
    const reader = new FileReader()
    reader.onload = ( e ) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement( 'canvas' )
        let width = img.width
        let height = img.height

        // Calculate new dimensions
        if ( width > height ) {
          if ( width > maxWidth ) {
            height = Math.round( ( height * maxWidth ) / width )
            width = maxWidth
          }
        } else if ( height > maxHeight ) {
          width = Math.round( ( width * maxHeight ) / height )
          height = maxHeight
        }

        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext( '2d' )
        ctx.drawImage( img, 0, 0, width, height )

        const base64String = canvas.toDataURL( 'image/jpeg', quality )
        const base64Data = base64String.split( ',' )[1]
        resolve( base64Data )
      }
      img.src = e.target.result
    }
    reader.readAsDataURL( file )
  } )
}

export const handlePlyLayerOverride = async ( { 
  file,
  plyId,
  locationId,
  type,
  selectedIssue,
  resourcePath,
  attributePath,
  statusSource,
  shellId,
  subAssemblyJobId,
  userInfo,
  isNextSA
} ) => {
  const correctedStatusPath = type === 'Tolerance' ? 'placement/toleranceStatus' : 'ocr/matchStatus'
  const correctedStatus = type === 'Tolerance' ? 'withinTolerance' : 'true'
  const overrideServiceAlias = type === 'Tolerance' ? 'plyToleranceOverride' : 'plyOcrOverride'
  const overrideServicePath = type === 'Tolerance' ? 'toleranceOverride' : 'ocrOverride'

  try {
    
    const res = await GlobalState.dispatch( async ( dispatch, getState, { api } ) => {
      const json = await api.interface( process.env.SYSTEM_ID, 'location' )
        .obj( locationId )
        .srv( overrideServiceAlias )
        .post( overrideServicePath,
          {
            plyId,
            shellId,
            subAssemblyJobId,
            userInfo,
            file,
            metaData : {
              selectedIssue
            },
            resourcePath,
            attributePath,
            imageSource : statusSource,
            statusPath : correctedStatusPath,
            status : correctedStatus,
            useNextSubAssemblyJob : isNextSA
          }
        )
      return json
    } )
    console.log( 'Override response', res )
    Toast.success( 'Override successful' )
  } catch ( err ) {
    console.error( 'Error in override', err )
    if ( err.message === 'Forbidden' && err.status === 403 ) {
      Toast.error( 'Permission denied to override this ply layer' )
    } else {
      Toast.error( 'Override failed' )
    }
  }
}

export const canPlyLayerOverride = async ( {
  locationId,
  type,
} ) => {
  const overrideServiceAlias = type === 'Tolerance' ? 'plyToleranceOverride' : 'plyOcrOverride'
  const overrideServicePath = type === 'Tolerance' ? 'toleranceOverride' : 'ocrOverride'

  try {
    const res = await GlobalState.dispatch( async ( dispatch, getState, { api } ) => {
      return api.interface( process.env.SYSTEM_ID, 'location' )
        .obj( locationId )
        .srv( overrideServiceAlias )
        .post( overrideServicePath, null, { test : true } )
    } )
    return res.allowed
  } catch ( err ) {
    console.error( 'Error in checkPlyLayerOverride', err )
    return false
  }
}
